import { post } from 'app/shared/utils/fetchr';
import { useQuery } from '@tanstack/react-query';
import { ALERT_QUERY_KEYS } from 'app/modules/alerts/queries/keys';
import { useSelector } from 'react-redux';
import { selectHasReadAlertsPermission } from 'app/modules/session/selectors';
import { GetInstrumentsResponse } from 'app/modules/instruments/responses';
import { PaginationPayload } from 'app/shared/pagination/models';
import { selectDLEnabledForInstrumentList } from 'app/shared/featureFlags/selectors';
import { useEffect, useState } from 'react';
import { ColdStorageLoadingStatus } from 'app/shared/utils/fastArchHelpers';
import { useGetAlert } from 'app/modules/alerts/queries/useGetAlert';
import { GetInstrumentsCachedPayload } from 'app/modules/instruments/requests';

interface LegacyGetAlertInstrumentsResponse {
  count: number | null;
  instruments: GetInstrumentsResponse['txn_instruments'];
}

type GetAlertInstrumentsResponse =
  | GetInstrumentsResponse
  | LegacyGetAlertInstrumentsResponse;

const isLegacyResponse = (
  response: GetAlertInstrumentsResponse,
): response is LegacyGetAlertInstrumentsResponse => {
  return 'instruments' in response;
};

export const useGetAlertInstruments = (
  id: number,
  payload: PaginationPayload,
  enabled: boolean = true,
) => {
  const [prevColdStorageLoadingStatus, setPrevColdStorageLoadingStatus] =
    useState<ColdStorageLoadingStatus | null>(null);
  const useCacheEndpoint = useSelector(selectDLEnabledForInstrumentList);
  const { coldStorageLoading } = useGetAlert(id, {
    enabled: useCacheEndpoint,
  });
  const hasReadAlertsPermission = useSelector(selectHasReadAlertsPermission);
  const queryEnabled = hasReadAlertsPermission && payload.limit !== 0 && id > 0;

  const query = useQuery<LegacyGetAlertInstrumentsResponse>({
    queryKey: ALERT_QUERY_KEYS.getAlertInstruments(
      id,
      payload,
      useCacheEndpoint,
    ),
    queryFn: () => {
      if (useCacheEndpoint) {
        return post('/instruments/cached/list', {
          ...payload,
          alert_ids: [id],
        } satisfies GetInstrumentsCachedPayload);
      }
      return post<LegacyGetAlertInstrumentsResponse>(
        `/alerts/${id}/instruments`,
        payload,
      );
    },
    enabled: queryEnabled && enabled,
    staleTime: 1000,
    select: (
      data: GetAlertInstrumentsResponse,
    ): LegacyGetAlertInstrumentsResponse => {
      if (isLegacyResponse(data)) {
        return data;
      }
      // This is the cached response, change the format to return the same structure as the legacy response
      return {
        count: data.count,
        instruments: data.txn_instruments,
      };
    },
  });

  const { refetch } = query;
  useEffect(() => {
    // Re fetch instruments if we were waiting for the instruments to load from cold storage
    const wasLoading =
      prevColdStorageLoadingStatus === ColdStorageLoadingStatus.LOADING ||
      prevColdStorageLoadingStatus === ColdStorageLoadingStatus.LOADING_TIMEOUT;
    if (
      queryEnabled &&
      useCacheEndpoint &&
      wasLoading &&
      coldStorageLoading.instrument === ColdStorageLoadingStatus.LOADED
    ) {
      refetch();
    }
    setPrevColdStorageLoadingStatus(coldStorageLoading.instrument);
  }, [
    coldStorageLoading.instrument,
    prevColdStorageLoadingStatus,
    useCacheEndpoint,
    queryEnabled,
    refetch,
  ]);

  return query;
};
